import { useQuery } from "@tanstack/react-query"

import { getMapStations } from "@/services/portalApi"

const stationQueryKeys = {
  all: ["stations"] as const,
}

// The station-health worker rewrites the cached station list on a 5-minute
// CronJob (apps/partnerservice/k8s/base/cronjob-station-health.yaml), so most
// polls at this interval find nothing new. That is deliberate: the poll clock
// and the cron clock are unaligned, so an interval matching the worker's own
// cadence would leave a viewer up to ten minutes behind. Checking every minute
// caps the lag at roughly one.
//
// The wasted polls are close to free on the client. react-query's structural
// sharing compares the decoded result against the previous one and, when they
// are equal, hands back the *previous* object reference — even though
// getMapStations decodes a brand-new array from the ArrayBuffer every call. So
// an unchanged poll causes no re-render, and the deck.gl layer memos in
// MapContent (which key on the stations array) do not rebuild.
//
// They are not free on the wire: the packed payload is ~25KB and compresses
// badly, being mostly float pairs, so this is ~1.5MB/hour per open map tab. If
// that ever becomes the constraint, the fix is to stop conflating the check
// with the payload — poll a small endpoint returning just gathered_at and
// refetch the list only when it changes — rather than to slow this back down.
export const STATIONS_REFETCH_INTERVAL_MS = 1000 * 60

// useStations reads the packed station list backing both coverage-map layers,
// along with the worker's last-write time (the "Data As Of" timestamp).
//
// The coverage map is a long-lived page — users leave it open while panning
// around — so this polls rather than fetching once on mount. Two behaviours
// matter more than the poll itself and both come from react-query rather than
// from anything here:
//
//   - A refetch, failed or in flight, leaves the previous `data` in place (the
//     app sets a global `placeholderData: (prev) => prev` in main.tsx). That is
//     what keeps the old overlay on screen instead of blanking the map, and it
//     is why callers must drive their loading spinner off `isPending` (initial
//     load only) rather than `isFetching`.
//   - A failed poll is retried on react-query's own backoff, so a transient
//     backend blip resolves itself without the user reloading the page.
//
// Background polling is off: a coverage map left open in an unfocused tab has
// no viewer to serve, and react-query refetches on window focus anyway, so
// returning to the tab is itself a refresh.
export function useStations() {
  return useQuery({
    queryKey: stationQueryKeys.all,
    queryFn: getMapStations,
    refetchInterval: STATIONS_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: STATIONS_REFETCH_INTERVAL_MS,
  })
}
